import type { Products } from '@/api/user/getCurrentUser.ts';
import type { BlotterOrderEntry } from '@/store/blotter/types/BlotterOrderEntry.ts';
import {
  generateFalconOrder,
  generateFalconOrderFromBlotter,
  generateNightjarOrder,
  generateNightjarOrderFromBlotter,
  generateTwapOrder,
  generateTwapOrderFromBlotter,
} from '@/store/orders/algoOrders/state.ts';
import { generateCallOrder, generateCallOrderFromBlotterEntry } from '@/store/orders/call/state.ts';
import { generateFixingOrder, generateFixingOrderFromBlotter } from '@/store/orders/fixing/state.ts';
import { ALL_ORDER_TYPES, type Order, type OrderType } from '@/store/orders/state.ts';
import { generateStopLossOrder, generateStopLossOrderFromBlotter } from '@/store/orders/stopLoss/state.ts';
import { generateTakeProfitOrder, generateTakeProfitOrderFromBlotter } from '@/store/orders/takeProfitOrder/helpers.ts';
import type { OrderStateCurrencyPair } from '@/store/orders/types.ts';
import type { OrderMode } from '@/store/types.ts';

export const isOrderType = (value: string): value is OrderType => {
  return ALL_ORDER_TYPES.includes(value as OrderType);
};

export const isOrderFromProduct = (product: Products): product is Order => {
  return ALL_ORDER_TYPES.includes(product.type as OrderType);
};

export const ORDER_GENERATOR_BY_TYPE: Record<OrderType, () => Order> = {
  TakeProfitOrder: generateTakeProfitOrder,
  StopLossOrder: generateStopLossOrder,
  FixingOrder: generateFixingOrder,
  CallOrder: generateCallOrder,
  'Twap+Order': generateTwapOrder,
  FalconOrder: generateFalconOrder,
  NightjarOrder: generateNightjarOrder,
};

export const ORDER_GENERATOR_FROM_BLOTTER_BY_TYPE: Record<
  OrderType,
  (entry: BlotterOrderEntry, mode: OrderMode) => Order
> = {
  TakeProfitOrder: generateTakeProfitOrderFromBlotter,
  StopLossOrder: generateStopLossOrderFromBlotter,
  FixingOrder: generateFixingOrderFromBlotter,
  CallOrder: generateCallOrderFromBlotterEntry,
  'Twap+Order': generateTwapOrderFromBlotter,
  FalconOrder: generateFalconOrderFromBlotter,
  NightjarOrder: generateNightjarOrderFromBlotter,
};

export function getOMSCurrencyFromBlotterCurrency(currencyPair: string): OrderStateCurrencyPair | undefined {
  const ccys = currencyPair.split('/');

  if (ccys.length !== 2) {
    return undefined; // { currency1: 'EUR', currency2: 'USD' };
  }
  const [currency1 = '', currency2 = ''] = ccys;

  return { currency1, currency2 };
}
